// Контракт API пульта (задача D3): поток событий `GET /api/v1/stream` (SSE) — что сервер присылает, пока вкладка открыта.
//
// Поток не заменяет `GET`-маршруты, а подсказывает, что поменялось: статус Анны (пилюля в сайдбаре), статусы строк
// «Готовности», версия настроек запуска, ход диалога, счётчики «На проверку», прогон проверок. Состояния при подключении
// поток не присылает — только перемены после него: пульт перечитывает нужное `GET`-ом на каждое (пере)подключение.
//
// Кадр потока: `event: <type>`, `data: <весь StreamEvent как JSON>` (поле `type` есть и в `data`: разбирать можно
// только `data`), пустая строка. Первым идёт `retry: 5000`, раз в 25 с — комментарий `: ping` (браузер его не показывает:
// он нужен только промежуточным прокси). Когда сессия кончается, приходит событие `session_expired` с телом ошибки
// `{ error: { code: 'session_expired', messageRu } }`, и сервер закрывает поток — это не `StreamEvent`.
//
// Правило контракта: файл импортирует только `zod` и соседей (`./*.js`), типы — `z.infer`.
import { z } from 'zod';
import { MomentSchema } from './common.js';
import { CheckStatusSchema } from './readiness.js';
import { StatusSchema, VerdictSchema } from './status.js';

/** Типы событий потока по порядку брифа D3: по ним пульт подписывается (`addEventListener(type)`). */
export const STREAM_EVENT_TYPES = ['status', 'readiness', 'launch', 'dialog', 'counters', 'eval'] as const;
export const StreamEventTypeSchema = z.enum(STREAM_EVENT_TYPES);
export type StreamEventType = z.infer<typeof StreamEventTypeSchema>;

/** Имя события «сессия кончилась»: после него сервер закрывает поток, и пульт показывает окно входа. */
export const STREAM_SESSION_EXPIRED_EVENT = 'session_expired';

/** Строка «Готовности», у которой сменился статус: новый статус, «что не так» и момент проверки. */
export const ReadinessChangeSchema = z.object({
  id: z.string().min(1),
  status: CheckStatusSchema,
  whatRu: z.string(),
  checkedAt: MomentSchema,
});
export type ReadinessChange = z.infer<typeof ReadinessChangeSchema>;

/** Сколько ждёт проверки в «На проверку» на момент `at`: ответы, пробелы базы, уроки (D17 переиспользует эту форму). */
export const ReviewCountsSchema = z.object({
  answers: z.number().int().nonnegative(),
  gaps: z.number().int().nonnegative(),
  lessons: z.number().int().nonnegative(),
  at: MomentSchema,
});
export type ReviewCounts = z.infer<typeof ReviewCountsSchema>;

/**
 * Событие потока — объединение по `type`:
 * - `status` — весь статус Анны целиком, когда он изменился (поле `at` перемен не считается);
 * - `readiness` — строки, у которых сменился статус, и вердикт (он тоже может смениться без строк);
 * - `launch` — версия настроек запуска: момент их последнего сохранения, мс (0 — настройки ещё не сохраняли);
 * - `dialog` — диалог изменился: момент последней активности и шаг идущего хода (`null` — ход не идёт); источник — D8;
 * - `counters` — счётчики «На проверку» (только admin); источник — D17;
 * - `eval` — прогон проверочного набора: статус и сколько кейсов из скольких; источник — D19.
 */
export const StreamEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('status'), status: StatusSchema }),
  z.object({ type: z.literal('readiness'), changed: z.array(ReadinessChangeSchema), verdict: VerdictSchema }),
  z.object({ type: z.literal('launch'), version: z.number().int().nonnegative() }),
  z.object({
    type: z.literal('dialog'),
    id: z.string().min(1),
    lastActivityAt: MomentSchema,
    progress: z.object({ turnNo: z.number().int().nonnegative(), stepRu: z.string().nullable() }).nullable(),
  }),
  z.object({ type: z.literal('counters'), review: ReviewCountsSchema }),
  z.object({
    type: z.literal('eval'),
    runId: z.string().min(1),
    status: z.string(),
    done: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
  }),
]);
export type StreamEvent = z.infer<typeof StreamEventSchema>;
